import { Segmented } from 'antd';
import { observer } from 'mobx-react';
import React from 'react';
import { useContestStore } from '../../../contexts/mobx';
import { Contest, ContestTheme } from '../../../stores/contest';
import { Checklist } from '../checklist';
import { RuleTab } from '../checklist/rules';
import { ContestPreview, PreviewPolicyDraft } from '../contest-preview';
import style from './style.module.scss';

export type SidebarView = 'checklist' | 'preview';

interface ContestSidebarProps {
    contest: Contest;
    /** What the tab beside it opens on. */
    defaultView: SidebarView;
    hasUnsavedChanges: boolean;
    /** The Theme tab as edited, which the preview shows before any save. */
    theme: ContestTheme | null;
    /** The Policies tab as edited, which the preview shows the same way. */
    policies: PreviewPolicyDraft[];
    /** The contest's provinces as the General tab holds them, by name. */
    provinces: string[];
    /** The policy whose page the preview opens on, by its address in each language. */
    policyPage: Partial<Record<'fr' | 'en', string>> | null;
    /** Grows each time a tab asks for the preview, which then shows. */
    previewRequests: number;
    onOpenTab: (tab: RuleTab) => void;
}

/**
 * Beside the tabs that edit the contest: its publication checklist and its
 * preview. Each tab opens on its own default view, and a refused save brings
 * the checklist back with what it would have broken. The preview stays
 * mounted while hidden, so switching keeps what was done in it.
 */
export const ContestSidebar = observer(
    ({
        contest,
        defaultView,
        hasUnsavedChanges,
        theme,
        policies,
        provinces,
        policyPage,
        previewRequests,
        onOpenTab,
    }: ContestSidebarProps) => {
        const store = useContestStore();
        const [view, setView] = React.useState<SidebarView>(defaultView);

        React.useEffect(() => {
            setView(defaultView);
        }, [defaultView]);

        React.useEffect(() => {
            if (previewRequests > 0) {
                setView('preview');
            }
        }, [previewRequests]);

        React.useEffect(() => {
            if (store.refusedRules) {
                setView('checklist');
            }
        }, [store.refusedRules]);

        const failing = contest.isActive
            ? store.refusedRules?.length ?? 0
            : store.publication?.contestId === contest.id
            ? store.publication.rules.filter((rule) => !rule.ok).length
            : 0;

        return (
            <div className={style.sidebar}>
                <Segmented
                    block
                    value={view}
                    onChange={(value) => setView(value as SidebarView)}
                    options={[
                        {
                            value: 'checklist',
                            label: failing
                                ? `Checklist (${failing})`
                                : 'Checklist',
                        },
                        { value: 'preview', label: 'Preview' },
                    ]}
                />
                <div hidden={view !== 'checklist'}>
                    <Checklist
                        contest={contest}
                        hasUnsavedChanges={hasUnsavedChanges}
                        onOpenTab={onOpenTab}
                    />
                </div>
                <div hidden={view !== 'preview'}>
                    <ContestPreview
                        contest={contest}
                        theme={theme}
                        policies={policies}
                        provinces={provinces}
                        policyPage={policyPage}
                        policyRequest={previewRequests}
                    />
                </div>
            </div>
        );
    }
);
